import React, { useState, useEffect } from 'react';
import { DataTable, Card, CardHeader, CardContent, StatusBadge, Button, toast } from '@etherisc/ui-kit';
import { 
  ChirpsFile,
  ChirpsFilesResponse,
  FilesSortConfig,
  ChirpsFileMetadata
} from '../../types/files';
import { FilesService } from '../../services/climate/files/FilesService';
import { ClimateApiError } from '../../services/climate/base/ClimateApiError';
import { ChirpsDownloadDialog } from '../../components/chirps/ChirpsDownloadDialog';
import { RefreshCw, Database, Files, DownloadIcon, PlayIcon, AlertCircleIcon } from 'lucide-react';

interface FilesListPageProps {
  refreshInterval?: number; // Auto-refresh interval in milliseconds
}

// File size badge component using ui-kit StatusBadge
const FileSizeBadge: React.FC<{ sizeInMB: number }> = ({ sizeInMB }) => {
  const getSizeVariant = (size: number): 'success' | 'warning' | 'error' | 'info' | 'neutral' => {
    if (size < 10) return 'success';
    if (size < 100) return 'warning';
    return 'error';
  };

  return (
    <StatusBadge variant={getSizeVariant(sizeInMB)}>
      {sizeInMB.toFixed(2)} MB
    </StatusBadge>
  );
};

// Metadata display component using ui-kit StatusBadge
const MetadataDisplay: React.FC<{ metadata: ChirpsFileMetadata }> = ({ metadata }) => {
  const entries = Object.entries(metadata).filter(([, value]) => value !== 'unknown');
  
  if (entries.length === 0) {
    return <span className="text-gray-400 text-sm">No metadata</span>;
  }

  return (
    <div className="flex flex-wrap gap-1">
      {entries.slice(0, 3).map(([key, value]) => (
        <StatusBadge key={key} variant="neutral">
          {key}: {value}
        </StatusBadge>
      ))}
      {entries.length > 3 && (
        <span className="text-xs text-gray-400">+{entries.length - 3} more</span>
      )}
    </div>
  );
};

const FilesListPage: React.FC<FilesListPageProps> = ({ 
  refreshInterval = 30000 // Default 30 seconds
}) => {
  const [files, setFiles] = useState<ChirpsFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortConfig] = useState<FilesSortConfig>({
    field: 'folder_path',
    direction: 'asc'
  });
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [generatingChunks, setGeneratingChunks] = useState(false);

  const filesService = new FilesService();

  // Fetch files data
  const fetchFiles = async (showLoadingState = true) => {
    try {
      if (showLoadingState) setLoading(true);

      const response: ChirpsFilesResponse = await filesService.getFiles();
      
      // Sort the files based on current sort configuration
      const sortedFiles = filesService.sortFiles(response.data, sortConfig);
      setFiles(sortedFiles);
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Failed to fetch files:', err);
      const errorMessage = err instanceof ClimateApiError ? err.message : 'Failed to load CHIRPS files';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Initial load and auto-refresh setup
  useEffect(() => {
    fetchFiles();

    // Set up auto-refresh if enabled
    if (refreshInterval > 0) {
      const interval = setInterval(() => {
        fetchFiles(false); // Don't show loading state for auto-refresh
      }, refreshInterval);

      return () => clearInterval(interval);
    }
  }, [refreshInterval, sortConfig]);

  // Manual refresh handler
  const handleRefresh = () => {
    fetchFiles();
  };

  // Open download dialog
  const handleDownloadClick = () => {
    setDownloadDialogOpen(true);
  };

  // Handle CHIRPS chunk generation
  const handleGenerateChunks = async () => {
    setConfirmDialogOpen(false);
    setGeneratingChunks(true);
    
    try {
      const result = await filesService.triggerChirpsChunkGeneration();
      toast.success(
        'CHIRPS chunk generation started',
        { description: `Job ID: ${result.job_id}. Expected processing time: ~${result.estimated_processing_time_minutes} minutes. Monitor progress in Job Management.` }
      );
    } catch (err) {
      console.error('Failed to trigger CHIRPS chunk generation:', err);
      const errorMessage = err instanceof ClimateApiError ? err.message : 'Failed to start CHIRPS chunk generation';
      toast.error(errorMessage);
    } finally {
      setGeneratingChunks(false);
    }
  };

  // Format timestamp for display
  const formatTimestamp = (timestamp: string): string => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Get relative time
  const getRelativeTime = (timestamp: string): string => {
    const now = new Date();
    const date = new Date(timestamp);
    const diffInDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    
    if (diffInDays === 0) return 'Today';
    if (diffInDays === 1) return 'Yesterday';
    if (diffInDays < 30) return `${diffInDays} days ago`;
    if (diffInDays < 365) return `${Math.floor(diffInDays / 30)} months ago`;
    return `${Math.floor(diffInDays / 365)} years ago`;
  };

  // Calculate statistics
  const statistics = files.length > 0 ? filesService.getFileStatistics(files) : null;

  // Define table columns
  const columns = [
    {
      id: 'folder_path',
      header: 'Folder Path',
      accessorKey: 'folder_path',
      enableSorting: true,
      size: 300,
      cell: ({ row }: { row: { original: ChirpsFile } }) => (
        <div className="flex items-center space-x-2">
          <Database className="h-4 w-4 text-blue-500" />
          <span className="font-mono text-sm">{row.original.folder_path}</span>
        </div>
      ),
    },
    {
      id: 'file_count',
      header: 'Files',
      accessorKey: 'file_count',
      enableSorting: true,
      size: 100,
      cell: ({ row }: { row: { original: ChirpsFile } }) => (
        <div className="flex items-center space-x-1">
          <Files className="h-4 w-4 text-gray-500" />
          <span className="font-medium">{row.original.file_count}</span>
        </div>
      ),
    },
    {
      id: 'total_size_mb',
      header: 'Total Size',
      accessorKey: 'total_size_mb',
      enableSorting: true,
      size: 120,
      cell: ({ row }: { row: { original: ChirpsFile } }) => (
        <FileSizeBadge sizeInMB={row.original.total_size_mb} />
      ),
    },
    {
      id: 'average_size_mb',
      header: 'Avg Size',
      accessorKey: 'average_size_mb',
      enableSorting: true,
      size: 120,
      cell: ({ row }: { row: { original: ChirpsFile } }) => (
        <span className="text-sm text-gray-600">
          {row.original.average_size_mb.toFixed(2)} MB
        </span>
      ),
    },
    {
      id: 'oldest_file_date',
      header: 'Oldest File',
      accessorKey: 'oldest_file_date',
      enableSorting: true,
      size: 150,
      cell: ({ row }: { row: { original: ChirpsFile } }) => (
        <div className="text-sm">
          <div>{formatTimestamp(row.original.oldest_file_date)}</div>
          <div className="text-xs text-gray-400">{getRelativeTime(row.original.oldest_file_date)}</div>
        </div>
      ),
    },
    {
      id: 'newest_file_date',
      header: 'Newest File',
      accessorKey: 'newest_file_date',
      enableSorting: true,
      size: 150,
      cell: ({ row }: { row: { original: ChirpsFile } }) => (
        <div className="text-sm">
          <div>{formatTimestamp(row.original.newest_file_date)}</div>
          <div className="text-xs text-gray-400">{getRelativeTime(row.original.newest_file_date)}</div>
        </div>
      ),
    },
    {
      id: 'metadata',
      header: 'Metadata',
      accessorKey: 'metadata',
      enableSorting: false,
      size: 250,
      cell: ({ row }: { row: { original: ChirpsFile } }) => (
        <MetadataDisplay metadata={row.original.metadata} />
      ),
    },
  ];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">CHIRPS Files</h1>
          <p className="text-gray-600 mt-1">
            Available CHIRPS data files on the R server
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            onClick={() => setConfirmDialogOpen(true)}
            disabled={generatingChunks}
            intent="primary"
            size="sm"
            className="flex items-center space-x-2"
          >
            <PlayIcon size={16} className={generatingChunks ? 'animate-spin' : ''} />
            <span>{generatingChunks ? 'Starting...' : 'Generate Chunks'}</span>
          </Button>
          <Button
            onClick={handleDownloadClick}
            intent="primary"
            size="sm"
            className="flex items-center space-x-2"
          >
            <DownloadIcon size={16} />
            <span>Download CHIRPS Files</span>
          </Button>
          <Button
            onClick={handleRefresh}
            disabled={loading}
            intent="outline"
            size="sm"
            className="flex items-center space-x-2"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            <span>Refresh</span>
          </Button>
        </div>
      </div>

      {/* Statistics Cards using ui-kit Card components */}
      {statistics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-blue-50">
            <CardHeader>
              <h3 className="text-sm font-medium text-gray-600">Total Folders</h3>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <p className="text-2xl font-bold text-blue-600">{statistics.total_folders}</p>
                <Database className="h-6 w-6 text-blue-600" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-green-50">
            <CardHeader>
              <h3 className="text-sm font-medium text-gray-600">Total Files</h3>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <p className="text-2xl font-bold text-green-600">{statistics.total_files}</p>
                <Files className="h-6 w-6 text-green-600" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-purple-50">
            <CardHeader>
              <h3 className="text-sm font-medium text-gray-600">Total Size</h3>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <p className="text-2xl font-bold text-purple-600">{statistics.total_size_mb.toFixed(2)} MB</p>
                <Database className="h-6 w-6 text-purple-600" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-orange-50">
            <CardHeader>
              <h3 className="text-sm font-medium text-gray-600">Avg Files/Folder</h3>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <p className="text-2xl font-bold text-orange-600">{statistics.average_files_per_folder.toFixed(1)}</p>
                <Files className="h-6 w-6 text-orange-600" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Last refresh info */}
      <div className="text-sm text-gray-500">
        Last updated: {lastRefresh.toLocaleTimeString()}
        {refreshInterval > 0 && (
          <span className="ml-2">
            (Auto-refresh every {Math.round(refreshInterval / 1000)}s)
          </span>
        )}
      </div>

      {/* Files Table */}
      <DataTable
        data={files}
        columns={columns}
        loading={loading}
        enableSorting={true}
        enableResizing={true}
      />

      {/* Download Dialog */}
      <ChirpsDownloadDialog
        isOpen={downloadDialogOpen}
        onClose={() => setDownloadDialogOpen(false)}
      />

      {/* Confirmation Dialog for CHIRPS Chunk Generation */}
      {confirmDialogOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-start gap-3 mb-4">
                <AlertCircleIcon className="h-6 w-6 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Generate CHIRPS Chunks
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    This will start a background job to regenerate CHIRPS parquet chunks for Zimbabwe.
                  </p>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-700">Region:</span>
                    <span className="font-medium text-gray-900">Zimbabwe</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-700">Expected Chunks:</span>
                    <span className="font-medium text-gray-900">72</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-700">Estimated Time:</span>
                    <span className="font-medium text-gray-900">~35 minutes</span>
                  </div>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-6">
                <p className="text-xs text-yellow-800">
                  The job will run in the background. You can monitor progress in the Job Management page.
                </p>
              </div>

              <div className="flex items-center gap-3 justify-end">
                <Button
                  onClick={() => setConfirmDialogOpen(false)}
                  intent="outline"
                  size="sm"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleGenerateChunks}
                  intent="primary"
                  size="sm"
                  className="flex items-center gap-2"
                >
                  <PlayIcon size={16} />
                  <span>Start Generation</span>
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FilesListPage; 